/**
 * Userpilot Tracking Hook
 *
 * This hook provides functions for tracking events and triggering surveys.
 * Safe to call from any component that needs to track user interactions.
 *
 * Make sure `useUserpilotInit` is called once at the app root before using this hook.
 *
 * Benefits:
 * - Better code discoverability (search for TRACK_EVENTS or SURVEY_CODES)
 * - Type-safe event-specific parameters
 * - Consistent pattern across all events
 * - Auto-adds userId and timestamp
 *
 * @example
 * ```typescript
 * import { useUserpilotTracking } from '../hooks/useUserpilotTracking';
 * import { TRACK_EVENTS, SURVEY_CODES } from '../userpilot';
 *
 * const MyComponent = () => {
 *   const { trackEvent, triggerSurvey } = useUserpilotTracking();
 *
 *   const handleRecordingStart = () => {
 *     trackEvent(TRACK_EVENTS.RECORDING_STARTED, {
 *       sessionId: '123',
 *       deviceId: 'default',
 *       deviceLabel: 'Built-in Microphone'
 *     });
 *   };
 *
 *   const handleSurvey = () => {
 *     triggerSurvey(SURVEY_CODES.POST_RECORDING_FEEDBACK);
 *   };
 * };
 * ```
 */

import { useCallback } from 'react';
import { useWidgetAuth } from '../context/AuthContext';
import { SURVEY_CODES } from '../userpilot';
import {
  isEnabledForUser,
  trackEvent as trackEventInternal,
  triggerSurvey as triggerSurveyInternal,
} from '../userpilot/internal/core';
import type {
  BaseEventParams,
  TrackedEvent,
} from '../userpilot/internal/types';

interface UseUserpilotTrackingReturn {
  /**
   * Track an event with type-safe parameters
   * Type inference works automatically from the event object
   *
   * User context (userId, customerId, brand, market) and timestamp
   * are automatically added - you don't need to provide them.
   *
   * @example
   * ```typescript
   * trackEvent(TRACK_EVENTS.RECORDING_STARTED, {
   *   sessionId: '123',
   *   deviceId: 'default',
   *   deviceLabel: 'Built-in Microphone'
   * });
   * ```
   */
  trackEvent: <TParams extends BaseEventParams>(
    event: TrackedEvent<TParams>,
    params?: Omit<
      TParams,
      'userId' | 'customerId' | 'brand' | 'market' | 'timestamp'
    > & {
      sessionId?: string | null;
    }
  ) => void;

  /**
   * Trigger a Userpilot survey
   *
   * @example
   * ```typescript
   * triggerSurvey(SURVEY_CODES.POST_RECORDING_FEEDBACK);
   * ```
   */
  triggerSurvey: (
    surveyCode: (typeof SURVEY_CODES)[keyof typeof SURVEY_CODES]
  ) => void;

  /**
   * Whether tracking is enabled for the current user
   */
  isTrackingEnabled: boolean;
}

/**
 * Hook for tracking Userpilot events and triggering surveys
 *
 * Automatically includes user context (userId) with all tracked events.
 * Safe to call from any component - no side effects, only provides tracking functions.
 */
export const useUserpilotTracking = (): UseUserpilotTrackingReturn => {
  const { user } = useWidgetAuth();

  const isTrackingEnabled = isEnabledForUser(user);

  /**
   * Add user context to event parameters
   * Automatically includes userId and organization context from auth
   * Note: Does not include PII (userName, userEmail) for compliance
   */
  const addUserContext = useCallback(
    <T extends BaseEventParams>(params?: T): T => {
      return {
        ...params,
        userId: user?.userId,
        customerId: user?.customerId,
        brand: user?.brand,
        market: user?.market,
      } as T;
    },
    [user]
  );

  /**
   * Track an event with automatic user context
   * Type inference works automatically from the event object
   * Automatically adds: userId, customerId, brand, market (no PII)
   */
  const trackEvent = useCallback(
    <TParams extends BaseEventParams>(
      event: TrackedEvent<TParams>,
      params?: Omit<
        TParams,
        'userId' | 'customerId' | 'brand' | 'market' | 'timestamp'
      > & {
        sessionId?: string | null;
      }
    ) => {
      if (!isTrackingEnabled) return;

      const paramsWithContext = addUserContext(params as TParams);

      trackEventInternal(event, paramsWithContext);
    },
    [addUserContext, isTrackingEnabled]
  );

  const triggerSurvey = useCallback(
    (surveyCode: (typeof SURVEY_CODES)[keyof typeof SURVEY_CODES]) => {
      if (!isTrackingEnabled) return;
      triggerSurveyInternal(surveyCode);
    },
    [isTrackingEnabled]
  );

  return {
    trackEvent,
    triggerSurvey,
    isTrackingEnabled,
  };
};
